import type { EnExperienceViewModel } from '@/models/enExperience';
import type { VideoMedia } from '@/wabunka-src/common/features/Featured/models/videoMedia';
import { COMMON_TITLE } from '@/wabunka-src/constants/metaTag';
import type { ExperienceListViewModel, ExperienceViewModel, MicroCMSExperienceViewModel } from '@/wabunka-src/features/Experience';
import type { StoryResponse } from '@/wabunka-src/features/Story';

import type { MicroCMSImage } from 'microcms-js-sdk';

type CustomPartExperiences =
  | {
      fieldId: 'mediumExps';
      experiences: MicroCMSExperienceViewModel[];
    }
  | {
      fieldId: 'smallExps';
      experiences: MicroCMSExperienceViewModel[];
    };

/**
 * microCMS「ドロワーコンテンツ」繰り返しフィールドの各項目
 *
 * NOTE: ADR に基づき、リッチテキスト内の `.drawer-trigger` の出現順と
 * この配列のインデックスを自動マッチングする設計。
 * トレードオフによる選択のため、他の実装の参考にしないこと。
 */
export interface DrawerContentItem {
  id: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revisedAt: string;
  title: string;
  contents: string;
}

export type CustomPart =
  | {
      fieldId: 'richTextEditor';
      richTextEditor: string;
    }
  | CustomPartExperiences
  | {
      fieldId: 'stories';
      stories: StoryResponse[];
    }
  | {
      fieldId: 'ctaButton';
      label: string;
      link: string;
    }
  | {
      fieldId: 'largeExps';
      experience: EnExperienceViewModel;
    }
  | {
      fieldId: 'imageMedia';
      pcImageMedias: MicroCMSImage[];
      spImageMedias: MicroCMSImage[];
    }
  | {
      fieldId: 'videoMedia';
      pcVideoMedia: VideoMedia;
      spVideoMedia: VideoMedia;
    };

export type CustomParts = CustomPart[];

export type ArticleContent =
  | CustomPart
  | {
      fieldId: 'itineraryParts';
      itineraryParts: CustomParts;
    }
  | {
      fieldId: 'drawerContents';
      drawerContents: DrawerContentItem[];
    };

export interface DrawerLink {
  fieldId: 'drawerLink';
  label: string;
  tabKey: string;
}

export type ArticleColumnContent =
  | {
      fieldId: 'richTextEditor';
      richTextEditor: string;
    }
  | DrawerLink
  | CustomPartExperiences
  | {
      fieldId: 'stories';
      stories: StoryResponse[];
    }
  | {
      fieldId: 'itineraryParts';
      itineraryParts: CustomParts;
    }
  | {
      fieldId: 'ctaButton';
      label: string;
      link: string;
    }
  | {
      fieldId: 'largeExps';
      experience: EnExperienceViewModel;
    }
  | {
      fieldId: 'imageMedia';
      pcImageMedias: MicroCMSImage[];
      spImageMedias: MicroCMSImage[];
    }
  | {
      fieldId: 'drawerContents';
      drawerContents: DrawerContentItem[];
    };

export interface ArticleColumn {
  fieldId: 'articleColumn';
  contents: ArticleColumnContent[];
}

export interface ArticleSection {
  fieldId: 'articleSection';
  columns: ArticleColumn[];
  reverseOrder: boolean;
  backgroundColor?: string[];
  /** 指定時はURL末尾に#{anchorLinkId}でこのセクションへスクロール可能 */
  anchorLinkId?: string;
}

export interface StoryContents {
  fieldId: 'storyContents';
  storyContents: (
    | {
        fieldId: 'storyContentTitle';
        title: string;
      }
    | {
        fieldId: 'stories';
        stories: StoryResponse[];
      }
  )[];
  backgroundColor?: string[];
}

export type Contents = (
  | ArticleSection
  | StoryContents
  | {
      fieldId: 'experienceSectionIDs';
      experienceSectionIDs: {
        fieldId: 'experienceSectionId';
        experienceSectionId: string;
      }[];
    }
)[];

/**
 * MicroCMS 返り値の型定義
 * NOTE: MicroCMS の以下ページで作成したフィールドと一致させておく
 * https://dev-calchez.microcms.io/apis/en-featured
 */
interface Featured {
  id: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revisedAt: string;
  /** コンテンツ種別。microCMS の仕様上、配列で返ってくる */
  type: string[];
  titleEn: string;
  titleJa: string;
  summary: string;
  backgroundWideMedia: MicroCMSImage;
  backgroundWideMedias: MicroCMSImage[];
  backgroundTallMedia: MicroCMSImage;
  backgroundTallMedias: MicroCMSImage[];
  contents: Contents;
  contentsBottom?: Contents;
  /** 旅程タグ設定（days / price はどちらかだけの設定もある） */
  itineraryTagSettings?: {
    days?: number;
    price?: string;
  };
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: MicroCMSImage;
}

export type FeaturedViewModel = Featured;

export const createDefaultFeaturedState = (): Featured => ({
  id: '',
  createdAt: '',
  updatedAt: '',
  publishedAt: '',
  revisedAt: '',
  type: [],
  titleEn: '',
  titleJa: '',
  summary: '',
  backgroundWideMedia: { url: '', height: 0, width: 0 },
  backgroundWideMedias: [],
  backgroundTallMedia: { url: '', height: 0, width: 0 },
  backgroundTallMedias: [],
  contents: [],
  metaTitle: '',
  metaDescription: '',
  ogImage: { url: '', height: 0, width: 0 },
});

/**
 * Contents 配列から全ての DrawerContentItem をフラット化して抽出する。
 * articleSection > columns > contents 内の drawerContents フィールドを走査する。
 */
export const extractDrawerContents = (contents: Contents): DrawerContentItem[] =>
  contents
    .filter((content): content is ArticleSection => content.fieldId === 'articleSection')
    .flatMap((content) => content.columns)
    .flatMap((column) => column.contents)
    .filter((item): item is { fieldId: 'drawerContents'; drawerContents: DrawerContentItem[] } => item.fieldId === 'drawerContents')
    .flatMap((item) => item.drawerContents);

export const findExperienceByMicroCMSExperience = (experiences: ExperienceListViewModel, microCMSExperience: MicroCMSExperienceViewModel): ExperienceViewModel | undefined => {
  return experiences.find((experience) => experience.slug === microCMSExperience.id);
};
export const filterExperiencesByMicroCMSExperiences = (experiences: ExperienceListViewModel, microCMSExperiences: MicroCMSExperienceViewModel[]): ExperienceListViewModel => {
  return microCMSExperiences
    .map((microCMSExperience) => findExperienceByMicroCMSExperience(experiences, microCMSExperience))
    .filter((experience): experience is ExperienceViewModel => experience !== undefined);
};

export const buildMetaData = (featured: FeaturedViewModel, options?: { ogUrl?: string; defaultOgImage?: string }) => {
  if (!featured) return {};

  const TITLE = `Discover Japan's Cultural Regions | ${COMMON_TITLE}`;
  const DESCRIPTION = "Explore Japan's diverse regions and their unique cultural traditions, crafts, and experiences curated by Wabunka.";

  const ogImage = featured.ogImage?.url || options?.defaultOgImage;
  const title = featured.metaTitle ?? TITLE;
  const description = featured.metaDescription ?? DESCRIPTION;

  const meta = {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogType: 'website' as const,
    twitterCard: 'summary_large_image' as const,
    twitterTitle: title,
    twitterDescription: description,
    ...(ogImage && { ogImage, twitterImage: ogImage }),
    ...(options?.ogUrl && { ogUrl: options.ogUrl }),
  };

  return meta;
};
